import { currentRange } from "./richTextCommands";
import { MergeAttributeClass } from "./sanitiseEmailHtml";

/**
 * Paragraph-level formatting for the rich text editor: block type, alignment, indent, lists
 * and Enter. Blocks are moved and restyled whole, so links and merge attributes inside them
 * are never rebuilt.
 */
export type Align = "left" | "center" | "right" | "justify";
export type ListTag = "UL" | "OL";

export const BlockOptions: { Key: string; Value: string }[] = [
  { Key: "P", Value: "Paragraph" },
  { Key: "BLOCKQUOTE", Value: "Quote" },
  { Key: "H1", Value: "Header 1" },
  { Key: "H2", Value: "Header 2" },
  { Key: "H3", Value: "Header 3" },
  { Key: "H4", Value: "Header 4" },
  { Key: "H5", Value: "Header 5" },
  { Key: "H6", Value: "Header 6" },
];

export interface BlockState {
  block: string;
  align: string;
  list: "" | ListTag;
}

export const DefaultBlockState: BlockState = { block: "P", align: "", list: "" };

const Chip = `span.${MergeAttributeClass}`;
const BlockNames = ["P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE"];
const BlockSelector = "p,h1,h2,h3,h4,h5,h6,blockquote,li";
const IndentStep = 40;
const MaxIndent = 400;

const isElement = (node: Node): node is HTMLElement => node.nodeType === 1;
const isList = (node: Node) => isElement(node) && (node.nodeName === "UL" || node.nodeName === "OL");
const isTextBlock = (node: Node) => isElement(node) && (BlockNames.includes(node.nodeName) || node.nodeName === "LI");
const isBlockLevel = (node: Node) => isTextBlock(node) || isList(node);

const indexOf = (node: Node) => Array.prototype.indexOf.call(node.parentNode?.childNodes ?? [], node) as number;

/** Nothing in the block to see: no words, no merge attribute. */
const isBlank = (block: Element) => !block.textContent && !block.querySelector(Chip);

function closestBlock(node: Node, root: HTMLElement) {
  for (let element = isElement(node) ? node : node.parentElement; element && element !== root; element = element.parentElement) {
    if (isTextBlock(element)) {
      return element;
    }
  }

  return null;
}

/** An empty block shows nothing and cannot hold the caret until it has a break in it. */
function fill(block: HTMLElement) {
  block.querySelectorAll("span,strong,em,b,i,u,s,strike,a").forEach((element) => {
    if (!element.textContent && !element.querySelector("br") && !element.matches(Chip) && !element.querySelector(Chip)) {
      element.remove();
    }
  });

  if (isBlank(block) && !block.querySelector("br")) {
    block.appendChild(document.createElement("br"));
  }
}

function setCaret(node: Node, offset: number) {
  const range = document.createRange();

  range.setStart(node, offset);
  range.collapse(true);
  window.getSelection()?.removeAllRanges();
  window.getSelection()?.addRange(range);
}

function caretAtStart(block: HTMLElement) {
  let node: Node = block;

  while (node.firstChild && isElement(node.firstChild) && node.firstChild.nodeName !== "BR" && !node.firstChild.matches(Chip)) {
    node = node.firstChild;
  }

  setCaret(node.firstChild?.nodeType === 3 ? node.firstChild : node, 0);
}

interface Anchor {
  node: Node;
  offset: number;
  next: Node | null;
  previous: Node | null;
}

const anchorAt = (node: Node, offset: number): Anchor => ({
  node,
  offset,
  next: isElement(node) ? node.childNodes[offset] ?? null : null,
  previous: isElement(node) && offset > 0 ? node.childNodes[offset - 1] : null,
});

function resolve(anchor: Anchor, root: HTMLElement): [Node, number] {
  const { node, offset, next, previous } = anchor;

  if (node.nodeType === 3 && root.contains(node)) {
    return [node, Math.min(offset, (node as Text).length)];
  }

  if (next?.parentNode && root.contains(next)) {
    return [next.parentNode, indexOf(next)];
  }

  if (previous?.parentNode && root.contains(previous)) {
    return [previous.parentNode, indexOf(previous) + 1];
  }

  return root.contains(node) ? [node, Math.min(offset, node.childNodes.length)] : [root, root.childNodes.length];
}

/** Runs the change, then puts the selection back on the same words however the blocks moved. */
function keepSelection(root: HTMLElement, change: () => void) {
  const range = currentRange(root);
  const start = range && anchorAt(range.startContainer, range.startOffset);
  const end = range && anchorAt(range.endContainer, range.endOffset);

  change();

  if (start && end) {
    const restored = document.createRange();
    const [startNode, startOffset] = resolve(start, root);
    const [endNode, endOffset] = resolve(end, root);

    restored.setStart(startNode, startOffset);
    restored.setEnd(endNode, endOffset);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(restored);
  }
}

/**
 * Puts loose words at the top of the text into paragraphs, a line to each, so every line is
 * a block that can be styled. Text already in blocks is left as it is.
 */
export function normaliseBlocks(root: HTMLElement) {
  const nodes = Array.from(root.childNodes);
  const hasBlocks = nodes.some(isBlockLevel);
  let line: Node[] = [];

  const flush = (before: Node | null) => {
    const blankText = line.every((node) => node.nodeType === 3 && !(node.textContent ?? "").trim());

    if (line.length > 0 && !(hasBlocks && blankText)) {
      const paragraph = document.createElement("p");

      root.insertBefore(paragraph, before);
      line.forEach((node) => paragraph.appendChild(node));
    } else {
      line.forEach((node) => node.parentNode?.removeChild(node));
    }

    line = [];
  };

  nodes.forEach((node) => {
    if (isBlockLevel(node)) {
      flush(node);
    } else if (node.nodeName === "BR") {
      if (line.length > 0) {
        flush(node.nextSibling);
        node.parentNode?.removeChild(node);
      } else {
        line.push(node);
        flush(node.nextSibling);
      }
    } else {
      line.push(node);
    }
  });

  flush(null);
}

function selectedBlocks(root: HTMLElement, range: Range) {
  const candidates = Array.from(root.querySelectorAll(BlockSelector)).filter((block) => {
    if (!range.intersectsNode(block)) {
      return false;
    }

    if (range.collapsed) {
      return true;
    }

    const leading = document.createRange();

    leading.setStart(block, 0);
    leading.setEnd(range.endContainer, range.endOffset);

    // A selection that stops at the very start of a block has not reached into it.
    return leading.toString() !== "" || block.contains(range.startContainer);
  }) as HTMLElement[];

  return candidates.filter(
    (block) => !candidates.some((inner) => inner !== block && block.contains(inner) && inner.contains(range.commonAncestorContainer))
  );
}

/** Makes the blocks under the selection, then hands them to the change. False when there is no selection in the text. */
function withBlocks(root: HTMLElement, change: (blocks: HTMLElement[]) => void) {
  if (!currentRange(root)) {
    return false;
  }

  if (root.childNodes.length === 0) {
    const paragraph = document.createElement("p");

    paragraph.appendChild(document.createElement("br"));
    root.appendChild(paragraph);
    setCaret(paragraph, 0);
  }

  keepSelection(root, () => normaliseBlocks(root));

  const range = currentRange(root);
  const blocks = range ? selectedBlocks(root, range) : [];

  if (blocks.length === 0) {
    return false;
  }

  keepSelection(root, () => change(blocks));
  return true;
}

function copyStyle(from: HTMLElement, to: HTMLElement, keep?: string[]) {
  if (!from.getAttribute("style")) {
    return;
  }

  if (!keep) {
    to.setAttribute("style", from.getAttribute("style") as string);
    return;
  }

  keep.forEach((property) => {
    const value = from.style.getPropertyValue(property);

    if (value) {
      to.style.setProperty(property, value);
    }
  });
}

function replaceTag(element: HTMLElement, tag: string, keep?: string[]) {
  const replacement = document.createElement(tag.toLowerCase());

  copyStyle(element, replacement, keep);

  while (element.firstChild) {
    replacement.appendChild(element.firstChild);
  }

  element.replaceWith(replacement);
  return replacement;
}

function tidyStyle(element: HTMLElement) {
  if (!element.getAttribute("style")) {
    element.removeAttribute("style");
  }
}

/** Turns each paragraph, heading or quote under the selection into the given kind. */
export function setBlockType(root: HTMLElement, tag: string) {
  return withBlocks(root, (blocks) => {
    blocks.forEach((block) => {
      if (block.nodeName !== "LI" && block.nodeName !== tag) {
        replaceTag(block, tag);
      }
    });
  });
}

/** Aligns the blocks under the selection, or takes the alignment off when they all have it. */
export function setAlign(root: HTMLElement, align: Align) {
  return withBlocks(root, (blocks) => {
    const all = blocks.every((block) => block.style.textAlign === align);

    blocks.forEach((block) => {
      if (all) {
        block.style.removeProperty("text-align");
      } else {
        block.style.textAlign = align;
      }

      tidyStyle(block);
    });
  });
}

/** Moves a list item into the list of the item before it. A first item has none, so it gets a bulletless holder. */
function nestItem(item: HTMLElement) {
  const list = item.parentElement;
  let previous = item.previousElementSibling;

  if (!list) {
    return;
  }

  if (!previous || previous.nodeName !== "LI") {
    previous = document.createElement("li");
    (previous as HTMLElement).style.listStyleType = "none";
    list.insertBefore(previous, item);
  }

  const last = previous.lastElementChild;
  const nested = last && isList(last) ? last : previous.appendChild(document.createElement(list.nodeName.toLowerCase()));

  nested.appendChild(item);
}

/** Moves an item of a nested list up a level. False for an item at the top level. */
function unnestItem(item: HTMLElement) {
  const list = item.parentElement;
  const holder = list?.parentElement;

  if (!list || !holder || holder.nodeName !== "LI" || !holder.parentNode) {
    return false;
  }

  const followers: Node[] = [];

  for (let sibling = item.nextSibling; sibling; sibling = sibling.nextSibling) {
    followers.push(sibling);
  }

  if (followers.length > 0) {
    const last = item.lastElementChild;
    const nested = last && isList(last) ? last : item.appendChild(document.createElement(list.nodeName.toLowerCase()));

    followers.forEach((follower) => nested.appendChild(follower));
  }

  holder.parentNode.insertBefore(item, holder.nextSibling);

  if (!list.firstChild) {
    list.remove();
  }

  // A holder made for a first item has nothing left in it once that item is out.
  if (!holder.firstChild) {
    holder.remove();
  }

  return true;
}

/** Makes each top-level list item a paragraph again, splitting its list around it. */
function unwrapItems(root: HTMLElement, items: HTMLElement[]) {
  const made: HTMLElement[] = [];

  items.forEach((item) => {
    const list = item.parentElement;

    if (!list || list.parentElement !== root) {
      return;
    }

    const before = list.cloneNode(false) as HTMLElement;
    const paragraph = document.createElement("p");
    const nested: Node[] = [];

    while (list.firstChild && list.firstChild !== item) {
      before.appendChild(list.firstChild);
    }

    if (before.firstChild) {
      list.before(before);
    }

    copyStyle(item, paragraph, ["text-align"]);

    Array.from(item.childNodes).forEach((child) => (isList(child) ? nested.push(child) : paragraph.appendChild(child)));
    fill(paragraph);
    list.before(paragraph);
    nested.forEach((child) => list.before(child));
    item.remove();

    if (!list.firstChild) {
      list.remove();
    }

    made.push(paragraph);
  });

  return made;
}

/** Indents the blocks under the selection a step, or nests the list items. */
export function indent(root: HTMLElement) {
  return withBlocks(root, (blocks) => {
    blocks.forEach((block) => {
      if (block.nodeName === "LI") {
        nestItem(block);
        return;
      }

      const next = Math.min(MaxIndent, (parseInt(block.style.paddingLeft, 10) || 0) + IndentStep);

      block.style.paddingLeft = `${next}px`;
    });
  });
}

/** Takes a step of indent off the blocks under the selection, or moves list items up a level. */
export function outdent(root: HTMLElement) {
  return withBlocks(root, (blocks) => {
    blocks.forEach((block) => {
      if (block.nodeName === "LI") {
        unnestItem(block);
        return;
      }

      const next = Math.max(0, (parseInt(block.style.paddingLeft, 10) || 0) - IndentStep);

      if (next === 0) {
        block.style.removeProperty("padding-left");
      } else {
        block.style.paddingLeft = `${next}px`;
      }

      tidyStyle(block);
    });
  });
}

/** Makes the blocks under the selection a list, changes the list they are in, or takes them out of it. */
export function toggleList(root: HTMLElement, tag: ListTag) {
  return withBlocks(root, (blocks) => {
    const items = blocks.filter((block) => block.nodeName === "LI");

    if (items.length === blocks.length) {
      if (items.every((item) => item.parentElement?.nodeName === tag)) {
        unwrapItems(root, items);
      } else {
        new Set(items.map((item) => item.parentElement as HTMLElement)).forEach((list) => {
          if (list.nodeName !== tag) {
            replaceTag(list, tag);
          }
        });
      }

      return;
    }

    blocks.forEach((block) => {
      if (block.nodeName === "LI") {
        return;
      }

      let list = block.previousElementSibling;

      if (!list || list.nodeName !== tag) {
        list = document.createElement(tag.toLowerCase());
        block.before(list);
      }

      list.appendChild(replaceTag(block, "li", ["text-align"]));
    });
  });
}

/** What the block holding the caret, or the start of the selection, is. */
export function blockStateAt(root: HTMLElement): BlockState {
  const range = currentRange(root);
  const block = range && closestBlock(range.startContainer, root);

  if (!block) {
    return DefaultBlockState;
  }

  const inList = block.nodeName === "LI";

  return {
    block: inList ? "P" : block.nodeName,
    align: block.style.textAlign,
    list: inList ? ((block.parentElement?.nodeName ?? "") as ListTag) : "",
  };
}

/** Enter: ends the block at the caret and starts the next, or ends a list on an empty item. False when there is no caret in the text. */
export function splitBlock(root: HTMLElement) {
  let range = currentRange(root);

  if (!range) {
    return false;
  }

  if (!range.collapsed) {
    range.deleteContents();
  }

  if (root.childNodes.length === 0) {
    const first = document.createElement("p");
    const second = document.createElement("p");

    first.appendChild(document.createElement("br"));
    second.appendChild(document.createElement("br"));
    root.append(first, second);
    caretAtStart(second);
    return true;
  }

  keepSelection(root, () => normaliseBlocks(root));
  range = currentRange(root);

  const block = range && closestBlock(range.startContainer, root);

  if (!range || !block) {
    return false;
  }

  if (block.nodeName === "LI" && isBlank(block) && !block.querySelector("ul,ol")) {
    const [paragraph] = unnestItem(block) ? [block] : unwrapItems(root, [block]);

    if (paragraph) {
      caretAtStart(paragraph);
    }

    return true;
  }

  const tail = document.createRange();

  tail.setStart(range.startContainer, range.startOffset);
  tail.setEnd(block, block.childNodes.length);

  const rest = tail.extractContents();
  const atEnd = !rest.textContent && !rest.querySelector(`${Chip},ul,ol`);
  const heading = /^H[1-6]$/.test(block.nodeName);
  const next = document.createElement(heading && atEnd ? "p" : block.nodeName.toLowerCase());

  copyStyle(block, next, heading && atEnd ? ["text-align"] : undefined);

  if (!atEnd) {
    next.appendChild(rest);
  }

  fill(block);
  fill(next);
  block.after(next);
  caretAtStart(next);
  return true;
}

/** Whether pasted content brings blocks of its own. */
export const hasBlocks = (fragment: DocumentFragment) => Boolean(fragment.querySelector(`${BlockSelector},ul,ol`));

/** The pasted content as words and line breaks, for a place that cannot take blocks. */
export function flattenBlocks(fragment: DocumentFragment) {
  const flat = document.createDocumentFragment();

  const walk = (source: Node) => {
    source.childNodes.forEach((node) => {
      if (isBlockLevel(node)) {
        if (flat.lastChild && flat.lastChild.nodeName !== "BR") {
          flat.appendChild(document.createElement("br"));
        }

        walk(node);
      } else {
        flat.appendChild(node.cloneNode(true));
      }
    });
  };

  walk(fragment);
  return flat;
}

/** Puts pasted blocks in at the caret, splitting the paragraph it is in. False when the caret is in a list item. */
export function insertBlocks(root: HTMLElement, fragment: DocumentFragment) {
  let range = currentRange(root);

  if (!range) {
    return false;
  }

  if (!range.collapsed) {
    range.deleteContents();
  }

  keepSelection(root, () => normaliseBlocks(root));
  range = currentRange(root);

  const block = range && closestBlock(range.startContainer, root);

  if (block?.nodeName === "LI") {
    return false;
  }

  const last = fragment.lastChild;

  if (!range || !block) {
    const at = range && range.startContainer === root ? root.childNodes[range.startOffset] ?? null : null;

    root.insertBefore(fragment, at);
  } else {
    const tail = document.createRange();

    tail.setStart(range.startContainer, range.startOffset);
    tail.setEnd(block, block.childNodes.length);

    const second = block.cloneNode(false) as HTMLElement;

    second.appendChild(tail.extractContents());
    fill(block);
    fill(second);
    block.after(second);
    second.before(fragment);

    [block, second].forEach((half) => {
      if (isBlank(half)) {
        half.remove();
      }
    });
  }

  keepSelection(root, () => normaliseBlocks(root));

  if (last?.parentNode) {
    const caret = document.createRange();

    if (isBlockLevel(last)) {
      caret.selectNodeContents(last);
      caret.collapse(false);
    } else {
      caret.setStartAfter(last);
      caret.collapse(true);
    }

    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(caret);
  }

  return true;
}
